import { makeKindKeyStringFromKindKey } from '@gonfalon/contexts/kind-key/makeKindKeyString';
import type { Map } from '@gonfalon/immutable-shim/immutableShim';
import type { AnyAction } from 'redux';
import { combineReducers } from 'redux';

import type { ContextOptionsAction, ContextTargetsAction } from '#actions/contexts';
import type { ContextItem, ContextItemsEntities } from '#components/Contexts/types';
import { createRequestReducerByKey } from '#reducers/createRequestReducer';
import { currentEnvironmentKeySelector, currentProjectKeySelector } from '#reducers/projects';
import registry from '#reducers/registry';
import type { GlobalState } from '#reducers/state';
import type { Request } from '#utils/requestUtils';
import { createRequest } from '#utils/requestUtils';

// MIGRATION (`migrate-contexts-epic-to-react-query`): this slice and the contexts epic feed the
// flag-OFF path only; the flag-ON path reads context data via React Query (see #epics/contexts).
// Remove this slice, its selectors, `#actions/contexts`, and #epics/contexts once the flag is gone.
import '#epics/contexts';

export type { ContextsSelectorProps } from '#reducers/contextSelectors';

const contextEntities = (state: ContextItemsEntities = {}, action: ContextOptionsAction | ContextTargetsAction) => {
  switch (action.type) {
    case 'contexts/LOAD_TARGET_BATCH_DONE':
    case 'contexts/SEARCH_CONTEXTS_DONE':
      return { ...state, ...action.contextsResponse.entities };
    default:
      return state;
  }
};

const contextRequests = (state: ContextItemsRequests = {}, action: ContextOptionsAction | ContextTargetsAction) => {
  switch (action.type) {
    // contexts loaded in batch by key
    case 'contexts/LOAD_TARGET':
      const kindKey = makeKindKeyStringFromKindKey(action.target);
      return {
        ...state,
        [kindKey]: createRequest({
          isFetching: true,
          error: null,
        }),
      };
    case 'contexts/LOAD_TARGET_BATCH_DONE':
    case 'contexts/LOAD_TARGET_BATCH_FAILED':
      const newTargetRequests = action.targets.reduce((accum, targetKindKey) => {
        const kindKeyString = makeKindKeyStringFromKindKey(targetKindKey);
        return {
          ...accum,
          [kindKeyString]: createRequest({
            isFetching: false,
            lastFetched: action.timestamp,
            error: null,
          }),
        };
      }, {});

      return {
        ...state,
        ...newTargetRequests,
      };

    default:
      return state;
  }
};

// Another view of the state of context requests by view ID.
const contextViewStatuses = createRequestReducerByKey(
  ['contexts/LOAD_TARGET', 'contexts/LOAD_TARGET_BATCH_DONE', 'contexts/LOAD_TARGET_BATCH_FAILED'],
  (action) => action.viewId,
);

type ContextOptionsByContextKind = {
  [id: string]: { contexts: ContextItem[]; isLoading: boolean };
};
const contextOptions = (state: ContextOptionsByContextKind = {}, action: ContextOptionsAction) => {
  switch (action.type) {
    case 'contexts/SEARCH_CONTEXTS':
      return { ...state, [action.id]: { contexts: [], isLoading: true } };
    case 'contexts/SEARCH_CONTEXTS_DONE':
      return { ...state, [action.id]: { contexts: action.contextsResponse.items, isLoading: false } };
    case 'contexts/SEARCH_CONTEXTS_FAILED':
      return { ...state, [action.id]: { contexts: [], isLoading: false } };
    case 'contexts/CLEAR_CONTEXTS_OPTIONS':
      const updatedState = { ...state };
      delete updatedState[action.id];
      return updatedState;
    default:
      return state;
  }
};

type ContextItemsRequests = { [kindKey: string]: Request };
type ContextsState = {
  entities: ContextItemsEntities;
  requests: ContextItemsRequests;
  viewStatuses: Map<string, Request>;
  optionsById: ContextOptionsByContextKind;
};
const contexts = combineReducers({
  entities: contextEntities,
  requests: contextRequests,
  viewStatuses: contextViewStatuses,
  optionsById: contextOptions,
});

import type { ContextsSelectorProps } from '#reducers/contextSelectors';

type ProjectAndEnvAction = AnyAction & ContextsSelectorProps;
type ContextsByProjectAndEnvironmentKeyState = { [s: string]: ContextsState };

export const contextsByProjectAndEnvironmentKey = (
  state: ContextsByProjectAndEnvironmentKeyState = {},
  action: ProjectAndEnvAction,
) =>
  action.projKey && action.envKey
    ? {
        ...state,
        [`${action.projKey}/${action.envKey}`]: contexts(state[`${action.projKey}/${action.envKey}`], action),
      }
    : state;

const contextsSelector = (state: GlobalState, props?: ContextsSelectorProps) => {
  let projKey;
  let environmentKey;
  if (!props) {
    projKey = currentProjectKeySelector(state);
    environmentKey = currentEnvironmentKeySelector(state);
  } else {
    projKey = props.projKey ? props.projKey : currentProjectKeySelector(state);
    environmentKey = props.envKey ? props.envKey : currentEnvironmentKeySelector(state);
  }
  return (
    state.contextsByProjectAndEnvironmentKey[`${projKey}/${environmentKey}`] || contexts(undefined, { type: null })
  );
};

const contextEntitiesSelector = (state: GlobalState, props: ContextsSelectorProps) =>
  contextsSelector(state, props).entities;

const contextsRequestsSelector = (state: GlobalState, props: ContextsSelectorProps) =>
  contextsSelector(state, props).requests;

export const contextRequestByKindKeySelector = (
  state: GlobalState,
  props: ContextsSelectorProps & { kindKey: string },
) => contextsRequestsSelector(state, props)[props.kindKey] || createRequest();

const contextViewStatusesSelector = (state: GlobalState, props: ContextsSelectorProps) =>
  contextsSelector(state, props).viewStatuses;

export const contextViewStatusByIdSelector = (state: GlobalState, props: ContextsSelectorProps & { viewId: string }) =>
  contextViewStatusesSelector(state, props).get(props.viewId, createRequest());

export const contextByKindKeySelector = (state: GlobalState, props: ContextsSelectorProps & { kindKey: string }) =>
  contextEntitiesSelector(state, props)[props.kindKey];

export const contextOptionsByIdSelectorWithoutProps = (
  state: GlobalState,
  projKey: string,
  envKey: string,
  id: string,
) => {
  const contextsByProjectAndEnvironmentKeyState =
    state.contextsByProjectAndEnvironmentKey[`${projKey}/${envKey}`] || contexts(undefined, { type: null });
  const optionsForId = contextsByProjectAndEnvironmentKeyState.optionsById[id];
  return {
    options:
      optionsForId?.contexts?.map(({ context }) => ({
        value: String(context.key),
        context,
      })) || [],
    isLoading: optionsForId?.isLoading || false,
  };
};

registry.addReducers({ contextsByProjectAndEnvironmentKey });

declare module '#reducers/state' {
  interface GlobalState {
    contextsByProjectAndEnvironmentKey: ReturnType<typeof contextsByProjectAndEnvironmentKey>;
  }
}
